import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { Sidebar as ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';

import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';


import {
  Send as SendIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';


import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import ArticleIcon from '@mui/icons-material/Article';
import PaymentsIcon from '@mui/icons-material/Payments';
import PolicyIcon from '@mui/icons-material/Policy';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { useThemeContext } from '../context/ThemeContext';
import { ChatBubbleOutline } from '@mui/icons-material';

interface SidebarProps {
  collapsed: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const theme = useTheme();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = mode === 'dark';

  // -----------------------------
  // Hiring Manager Menu
  // -----------------------------
  const hiringManagerMenu = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { id: 'companies', label: 'Companies', icon: <BusinessIcon />, path: '/companies' },
    { id: 'candidates', label: 'Candidates', icon: <PeopleIcon />, path: '/candidates' },
    { id: 'emails', label: 'Emails', icon: <SendIcon />, path: '/emails' },
    { id: 'masterEmails', label: 'Sequence Email ', icon: <MarkEmailReadIcon />, path: '/master-emails' },
    { id: 'leadlist', label: 'Lead List', icon: <GroupIcon />, path: '/lead-list' },
    { id: 'defaultPrompts', label: 'Default Prompts', icon: <AutoAwesomeIcon />, path: '/prompts' },
    { id: 'plans', label: 'Plans', icon: <ReceiptLongIcon />, path: '/plans' },
    { id: 'companyLegal', label: 'Terms & Privacy', icon: <PolicyIcon />, path: '/cms/legal?type=HIRING_MANAGER' },
    { id: 'homeVideo', label: 'Home Page Video', icon: <OndemandVideoIcon />, path: '/cms/home-video' },
  ];

  // -----------------------------
  // Job Seeker Menu
  // -----------------------------
  const jobSeekerMenu = [
    { id: 'visitiorListing', label: 'Visitors', icon: <PeopleIcon />, path: '/jobseekers/visitors' },
    { id: 'candidateListing', label: 'Job Seekers', icon: <PeopleIcon />, path: '/jobseekers/candidates' },
    { id: 'transcription', label: 'Transcription', icon: <ArticleIcon />, path: '/jobseekers/transcription' },
    // { id: 'applications', label: 'Applications', icon: <ReceiptLongIcon />, path: '/jobseekers/applications' },
    { id: 'module', label: 'Module', icon: <GroupIcon />, path: '/jobseekers/module' },
    { id: 'plans', label: 'Plans', icon: <ReceiptLongIcon />, path: '/jobseekers/plans' },
    { id: 'emails', label: 'Emails', icon: <SendIcon />, path: '/jobseekers/emails' },
    { id: 'prompts', label: 'Prompts', icon: <ChatBubbleOutline />, path: '/jobseekers/prompts' },
    { id: 'paymentHistory', label: 'Payment History', icon: <PaymentsIcon />, path: '/jobseekers/payments' },
    { id: 'candidateLegal', label: 'Terms & Privacy', icon: <PolicyIcon />, path: '/cms/legal?type=JOB_SEEKER' },
  ];

  // -----------------------------
  // Allow only ONE submenu open at a time
  // -----------------------------
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const toggleMenu = (menuId: string) => {
    setOpenMenu(prev => (prev === menuId ? null : menuId));
  };

  const sidebarBg = isDark
    ? alpha(theme.palette.background.paper, 0.8)
    : theme.palette.background.paper;

  const activeItemBg = isDark
    ? alpha(theme.palette.primary.main, 0.2)
    : alpha(theme.palette.primary.main, 0.1);

  const hoverBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : alpha(theme.palette.primary.main, 0.07);

  return (
    <ProSidebar
      collapsed={collapsed}
      width={collapsed ? '80px' : '260px'}
      style={{ height: '100%', boxShadow: '1px 0 8px rgba(0,0,0,0.1)' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: sidebarBg,
          color: theme.palette.text.primary,
        }}
      >
        {/* HEADER */}
        <Box sx={{ p: "21.5px", textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {!collapsed ? (
            <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
              Gobla Admin Panel
            </Typography>
          ) : (
            <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
              AP
            </Typography>
          )}
        </Box>

        {/* MENUS */}
        <Menu
          menuItemStyles={{
            button: ({ active }) => ({
              color: theme.palette.text.primary,
              backgroundColor: active ? activeItemBg : undefined,
              margin: '4px 8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': { backgroundColor: hoverBg },
            }),
            icon: ({ active }) => ({
              color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            }),
          }}
        >

          {/* -----------------------------
              Hiring Manager
          ----------------------------- */}
          <SubMenu
            label={!collapsed ? "Gobla GTM" : ""}
            icon={<WorkIcon />}
            open={openMenu === "hiringManager"}
            onClick={() => toggleMenu("hiringManager")}
          >
            {hiringManagerMenu.map(item => (
              <MenuItem
                key={item.id}
                active={item.path.includes('?')
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path
                }
                icon={item.icon}
                onClick={() => navigate(item.path)}
              >
                {!collapsed && item.label}
              </MenuItem>
            ))}
          </SubMenu>

          {/* -----------------------------
              Job Seeker  
          ----------------------------- */}
          {/* <SubMenu
            label={!collapsed ? "Job Seeker" : ""}
            icon={<GroupIcon />}
            open={openMenu === "jobSeeker"}
            onClick={() => toggleMenu("jobSeeker")}
          >
            {jobSeekerMenu.map(item => (
              <MenuItem
                key={item.id}
                active={item.path.includes('?')
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path
                }
                icon={item.icon}
                onClick={() => navigate(item.path)}
              >
                {!collapsed && item.label}
              </MenuItem>
            ))}
          </SubMenu> */}

        </Menu>
      </Box>
    </ProSidebar>
  );
};

export default Sidebar;
